/** Rule builder schema — settings per rule id */

export type RuleId = 'birthday' | 'hot' | 'cold' | 'overdue' | 'odd-even' | 'spread';

export interface ChipOption {
  v: string | number;
  l: string;
}

export interface SchemaFieldChip {
  id: string;
  label: string;
  type: 'chips';
  options: ChipOption[];
}

export interface SchemaFieldSlider {
  id: string;
  label: string;
  type: 'slider';
  min: number;
  max: number;
  step: number;
  suffix?: string;
}

export interface SchemaFieldToggle {
  id: string;
  label: string;
  type: 'toggle';
}

export type SchemaField = SchemaFieldChip | SchemaFieldSlider | SchemaFieldToggle;

export interface RuleSchemaEntry {
  info: string;
  stat: string;
  defaults: Record<string, string | number | boolean>;
  fields: SchemaField[];
}

export const RULE_SCHEMA: Record<RuleId, RuleSchemaEntry> = {
  birthday: {
    info:
      'Passes only if both your birthday month and day appear somewhere on the ticket (main numbers and extra ball when applicable). If month and day are the same number, one ball with that value is enough.',
    stat: 'Example: March 14 needs 3 and 14 among the drawn numbers you are evaluating.',
    defaults: { month: 3, day: 14 },
    fields: [
      { id: 'month', label: 'Birthday month', type: 'slider', min: 1, max: 12, step: 1 },
      { id: 'day', label: 'Birthday day', type: 'slider', min: 1, max: 31, step: 1 },
    ],
  },
  hot: {
    info:
      'Builds the set of numbers that appeared most often in the last N draws. Your ticket must include at least that many picks from that set among the main numbers.',
    stat: 'In the last 90 draws, the top 10 frequent numbers often cover multiple winning mains.',
    defaults: { window: 90, topK: 10, minHot: 2 },
    fields: [
      {
        id: 'window',
        label: 'Lookback window',
        type: 'chips',
        options: [
          { v: 30, l: '30' },
          { v: 90, l: '90' },
          { v: 180, l: '180' },
          { v: 365, l: '365' },
        ],
      },
      {
        id: 'topK',
        label: 'Hot pool size',
        type: 'slider',
        min: 3,
        max: 25,
        step: 1,
        suffix: ' nums',
      },
      {
        id: 'minHot',
        label: 'Min from hot pool',
        type: 'slider',
        min: 0,
        max: 5,
        step: 1,
        suffix: ' mains',
      },
    ],
  },
  cold: {
    info:
      'Builds the set of numbers that appeared least often in the last N draws. Your ticket must include at least that many picks from that set among the main numbers.',
    stat: 'Rare numbers in the window can still hit — this rule forces a few “cold” picks into the line.',
    defaults: { window: 90, bottomK: 10, minCold: 1 },
    fields: [
      {
        id: 'window',
        label: 'Lookback window',
        type: 'chips',
        options: [
          { v: 30, l: '30' },
          { v: 90, l: '90' },
          { v: 180, l: '180' },
          { v: 365, l: '365' },
        ],
      },
      {
        id: 'bottomK',
        label: 'Cold pool size',
        type: 'slider',
        min: 3,
        max: 25,
        step: 1,
        suffix: ' nums',
      },
      {
        id: 'minCold',
        label: 'Min from cold pool',
        type: 'slider',
        min: 0,
        max: 5,
        step: 1,
        suffix: ' mains',
      },
    ],
  },
  overdue: {
    info:
      'A number is “overdue” if it has not appeared in the main draw for at least the threshold number of past draws. Your ticket must include at least the chosen count of overdue numbers among mains.',
    stat: 'Long gaps happen by chance; this rule forces some “due” numbers into the line.',
    defaults: { threshold: 30, minOverdue: 1 },
    fields: [
      {
        id: 'threshold',
        label: 'Min draws absent',
        type: 'slider',
        min: 5,
        max: 90,
        step: 1,
        suffix: ' draws',
      },
      {
        id: 'minOverdue',
        label: 'Min overdue mains',
        type: 'slider',
        min: 0,
        max: 5,
        step: 1,
        suffix: ' nums',
      },
    ],
  },
  'odd-even': {
    info: 'Locks your picks to a specific ratio of odd vs even numbers on the five main balls.',
    stat: 'Many draws land on a 3:2 or 2:3 odd-even split on the main line.',
    defaults: { ratio: '3:2' },
    fields: [
      {
        id: 'ratio',
        label: 'Target ratio',
        type: 'chips',
        options: [
          { v: '5:0', l: '5:0' },
          { v: '4:1', l: '4:1' },
          { v: '3:2', l: '3:2' },
          { v: '2:3', l: '2:3' },
          { v: '1:4', l: '1:4' },
          { v: '0:5', l: '0:5' },
        ],
      },
    ],
  },
  spread: {
    info: 'Splits the main-number board into areas and asks your pick to cover several different areas.',
    stat: 'Balanced low-to-high coverage is a common manual-play heuristic.',
    defaults: { zones: 5, strictness: 'medium' },
    fields: [
      {
        id: 'zones',
        label: 'Number areas',
        type: 'chips',
        options: [
          { v: 3, l: '3' },
          { v: 5, l: '5' },
          { v: 7, l: '7' },
        ],
      },
      {
        id: 'strictness',
        label: 'Spread strength',
        type: 'chips',
        options: [
          { v: 'loose', l: 'Gentle' },
          { v: 'medium', l: 'Balanced' },
          { v: 'strict', l: 'Wide' },
        ],
      },
    ],
  },
};
